/**
 * Metadata confirmation step for ICP file imports.
 *
 * Rendered when POST /tests/file returns a 422 listing missing fields
 * (e.g. a renamed Triton CSV with no parsable filename metadata). Shows
 * whatever the parser DID extract, lets the user confirm or fill in the
 * gaps, then resubmits the same file with overrides.
 */
import { useState } from 'react';
import { Alert, Button, Group, Stack, Text, TextInput } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconAlertCircle } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { ProviderBadge } from './ProviderBadge';
import type {
  ICPFileImportMissingFieldsDetail,
  ICPFileImportOverrides,
} from '@/types/sensor';

export interface ImportFileConfirmProps {
  /** The structured 422 detail from the file import endpoint */
  detail: ICPFileImportMissingFieldsDetail;
  /** True while the resubmit mutation is in flight */
  submitting: boolean;
  /**
   * Error message from a failed (non-422) resubmit, rendered as a persistent
   * Alert inside the confirmation step so the failure has a durable surface
   * rather than only a transient notification.
   */
  errorMessage?: string | null;
  /** Resubmit the file with the confirmed metadata overrides */
  onConfirm: (overrides: ICPFileImportOverrides) => void;
  /** Abandon the confirmation step (back to file selection) */
  onCancel: () => void;
}

export function ImportFileConfirm({
  detail,
  submitting,
  errorMessage,
  onConfirm,
  onCancel,
}: ImportFileConfirmProps) {
  // The 422 detail's `parsed`/`warnings` are typed as present, but guard
  // defensively in case a future/edge server response omits them.
  const parsed = detail.parsed;
  const warnings = detail.warnings ?? [];

  const [testDate, setTestDate] = useState<string | null>(parsed?.test_date ?? null);
  const [barcode, setBarcode] = useState(parsed?.barcode ?? '');
  const [tankLabel, setTankLabel] = useState(parsed?.tank_label ?? '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!testDate || !barcode.trim()) {
      notifications.show({
        title: 'Validation Error',
        message: 'Test date and test code are required',
        color: 'red',
      });
      return;
    }

    onConfirm({
      test_date: testDate,
      barcode: barcode.trim(),
      tank_label: tankLabel.trim() || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        <Group gap="xs">
          <ProviderBadge provider={parsed?.provider} size="sm" />
          <Text size="sm" c="dimmed">
            Confirm the test details before importing
          </Text>
        </Group>

        <Alert icon={<IconAlertCircle size={16} />} color="yellow" variant="light">
          <Stack gap={4}>
            <Text size="sm">{detail.message}</Text>
            {warnings.map((warning) => (
              <Text key={warning} size="xs" c="dimmed">
                {warning}
              </Text>
            ))}
          </Stack>
        </Alert>

        {errorMessage && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            {errorMessage}
          </Alert>
        )}

        <DateInput
          label="Test date"
          placeholder="YYYY-MM-DD"
          value={testDate}
          onChange={setTestDate}
          valueFormat="YYYY-MM-DD"
          required
        />

        <TextInput
          label="Test code"
          placeholder="e.g. B-026X8x"
          value={barcode}
          onChange={(e) => setBarcode(e.currentTarget.value)}
          required
        />

        <TextInput
          label="Tank label"
          placeholder="Tank name on the report (optional)"
          value={tankLabel}
          onChange={(e) => setTankLabel(e.currentTarget.value)}
        />

        <Group grow>
          <Button variant="default" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" loading={submitting}>
            Confirm Import
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
